import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import './App.css';

import Login from './Login';
import Sidebar from './Sidebar';
import Dashboard from './Dashboard';
import ManageBookings from './ManageBookings';
import BookingDetail from './BookingDetail';
import ManageProviders from './ManageProviders';
import ProviderDetail from './ProviderDetail';
import ManageCustomers from './ManageCustomers';
import CustomerDetail from './CustomerDetail';
import ManageServices from './ManageServices';
import ManageSubcategories from './ManageSubcategories';
import ManageServiceItems from './ManageServiceItems';
import ManageCommissions from './ManageCommissions';
import ManageDepositHistory from './ManageDepositHistory';
import ManageDiscountCodes from './ManageDiscountCodes';
import DiscountDetail from './DiscountDetail';
import ManageConversations from './ManageConversations';
import MessageDetail from './MessageDetail';
import ManageReviews from './ManageReviews';
import ChangePassword from './ChangePassword';
import Settings from './Settings';
import Privacy from './Privacy';
import Terms from './Terms';
import TermsEN from './TermsEN';
import Contact from './Contact';
import ContactEN from './ContactEN';
import Home from './Home';
import HomeEN from './HomeEN';
import LaoDongPhoThong from './LaoDongPhoThong';
import LaoDongPhoThongEN from './LaoDongPhoThongEN';
import LaoDongNongNghiep from './LaoDongNongNghiep';
import LaoDongNongNghiepEN from './LaoDongNongNghiepEN';
import DichVuYTe from './DichVuYTe';
import DichVuYTeEN from './DichVuYTeEN';
import PrivacyEN from './PrivacyEN';
import Download from './Download';

function App() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Lỗi đăng xuất:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          Đang tải...
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Public routes - no authentication required */}
        <Route path="/" element={<Home />} />
        <Route path="/en" element={<HomeEN />} />
        <Route path="/en/general-labor" element={<LaoDongPhoThongEN />} />
        <Route path="/lao-dong-pho-thong" element={<LaoDongPhoThong />} />
        <Route path="/lao-dong-nong-nghiep" element={<LaoDongNongNghiep />} />
        <Route path="/en/agricultural-labor" element={<LaoDongNongNghiepEN />} />
        <Route path="/dich-vu-y-te" element={<DichVuYTe />} />
        <Route path="/en/healthcare-services" element={<DichVuYTeEN />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/en/privacy" element={<PrivacyEN />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/en/terms" element={<TermsEN />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/en/contact" element={<ContactEN />} />
        <Route path="/download" element={<Download />} />

        {/* Protected routes - authentication required */}
        <Route path="/admin/*" element={
          !user ? (
            <Login onLoginSuccess={() => setUser(auth.currentUser)} />
          ) : (
            <div className="App">
        <header className="admin-header">
          <div className="header-content">
            <Link to="/admin" className="header-title">
              <h1>Fconnect Admin</h1>
            </Link>
            <div className="user-info" ref={userMenuRef}>
              <div 
                className="user-menu-trigger"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <span>{user.email}</span>
                <i className="arrow-down"></i>
              </div>
              {showUserMenu && (
                <div className="user-menu">
                  <Link to="/admin/change-password" className="user-menu-item" onClick={() => setShowUserMenu(false)}>
                    Thay đổi mật khẩu
                  </Link>
                  <button onClick={handleLogout} className="user-menu-item logout">
                    Đăng xuất
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        
        <div className="app-layout">
          <Sidebar />
          <main className="admin-main">
            <Routes>
              <Route index element={<Dashboard />} />
              <Route path="bookings" element={<ManageBookings />} />
              <Route path="bookings/:bookingId" element={<BookingDetail />} />
              <Route path="providers" element={<ManageProviders />} />
              <Route path="providers/:providerId" element={<ProviderDetail />} />
              <Route path="customers" element={<ManageCustomers />} />
              <Route path="customers/:customerId" element={<CustomerDetail />} />
              <Route path="manage-services" element={<ManageServices />} />
              <Route path="manage-subcategories" element={<ManageSubcategories />} />
              <Route path="manage-service-items" element={<ManageServiceItems />} />
              <Route path="manage-commissions" element={<ManageCommissions />} />
              <Route path="manage-deposit-history" element={<ManageDepositHistory />} />
              <Route path="manage-discount-codes" element={<ManageDiscountCodes />} />
              <Route path="manage-discount-codes/:discountId" element={<DiscountDetail />} />
              <Route path="manage-conversations" element={<ManageConversations />} />
              <Route path="messages/:bookingId" element={<MessageDetail />} />
              <Route path="manage-reviews" element={<ManageReviews />} />
              <Route path="change-password" element={<ChangePassword />} />
              <Route path="settings" element={<Settings />} />
            </Routes>
          </main>
        </div>
      </div>
          )
        } />
      </Routes>
    </Router>
  );
}

export default App;
